import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { useLockBodyScroll } from '../utils/useLockBodyScroll';

const API_URL = (process.env.REACT_APP_API_URL || 'http://127.0.0.1:3001').replace(/\/$/, '');

const VillaDetailsModal = ({ isOpen, onClose, villaId, villa: villaFromList, onBook }) => {
    const [villa, setVilla] = useState(null);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);
    const [currentImageIndex, setCurrentImageIndex] = useState(0);

    useLockBodyScroll(isOpen);

    useEffect(() => {
        if (!isOpen) {
            setVilla(null);
            setError(null);
            setLoading(false);
            return;
        }

        const id = villaId || villaFromList?.id || villaFromList?._id;
        if (!id) {
            setError('Property ID is missing.');
            return;
        }

        // Show list data immediately while fetching full details
        if (villaFromList) {
            setVilla(villaFromList);
        }

        const fetchVilla = async () => {
            try {
                setLoading(true);
                setError(null);
                const res = await axios.get(`/api/villas/${id}`);
                setVilla(res.data);
                setCurrentImageIndex(0);
            } catch (err) {
                console.error('Error fetching villa:', err);
                setError('Could not load property details. Please try again.');
                if (!villaFromList) {
                    setVilla(null);
                }
            } finally {
                setLoading(false);
            }
        };

        fetchVilla();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, villaId]);

    if (!isOpen) return null;

    const getImageSrc = (img) => img?.startsWith('/') ? `${API_URL}${img}` : img;
    const formatPrice = (price) => {
        if (price === null || price === undefined || isNaN(Number(price))) {
            return 'On Request';
        }
        return new Intl.NumberFormat('en-IN', { style: 'currency', currency: 'INR', minimumFractionDigits: 0 }).format(Number(price));
    };

    const images = villa?.images?.length ? villa.images : ['https://images.unsplash.com/photo-1564501049412-61c2a3083791?w=1200&h=800&fit=crop'];

    return (
        <div
            className="fixed inset-0 bg-black/70 z-[100] flex items-center justify-center p-4 overflow-hidden"
            onClick={e => e.target === e.currentTarget && onClose()}
            role="dialog"
            aria-modal="true"
            aria-labelledby="villa-details-title"
        >
            <div className="bg-white rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col">
                <div className="bg-gradient-to-r from-brand-teal to-brand-cyan text-white p-5 flex justify-between items-center flex-shrink-0">
                    <h3 id="villa-details-title" className="text-2xl font-bold">{villa?.title || 'Property Details'}</h3>
                    <button type="button" onClick={onClose} className="text-white text-2xl hover:text-gray-200 transform hover:rotate-90 transition" aria-label="Close">
                        <i className="fas fa-times"></i>
                    </button>
                </div>

                <div className="overflow-y-auto flex-1 overscroll-contain">
                    {loading && !villa ? (
                        <div className="flex flex-col items-center justify-center py-24">
                            <i className="fas fa-spinner fa-spin text-4xl text-brand-teal mb-4"></i>
                            <p className="text-gray-600">Loading property details...</p>
                        </div>
                    ) : error && !villa ? (
                        <div className="text-center py-16 px-6">
                            <p className="text-gray-600 mb-4">{error}</p>
                            <button type="button" onClick={onClose} className="px-6 py-2 bg-brand-teal text-white rounded-lg hover:bg-brand-cyan">
                                Close
                            </button>
                        </div>
                    ) : villa ? (
                        <>
                            <div className="relative h-72 bg-gray-900">
                                <img src={getImageSrc(images[currentImageIndex])} alt={villa.title} className="w-full h-full object-cover" />
                                {images.length > 1 && (
                                    <>
                                        <button type="button" onClick={() => setCurrentImageIndex(i => (i - 1 + images.length) % images.length)} className="absolute left-3 top-1/2 -translate-y-1/2 bg-black/50 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/70 transition">
                                            <i className="fas fa-chevron-left"></i>
                                        </button>
                                        <button type="button" onClick={() => setCurrentImageIndex(i => (i + 1) % images.length)} className="absolute right-3 top-1/2 -translate-y-1/2 bg-black/50 text-white w-10 h-10 rounded-full flex items-center justify-center hover:bg-black/70 transition">
                                            <i className="fas fa-chevron-right"></i>
                                        </button>
                                        <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-2">
                                            {images.map((_, i) => (
                                                <button key={i} type="button" onClick={() => setCurrentImageIndex(i)} className={`w-2 h-2 rounded-full transition ${i === currentImageIndex ? 'bg-white' : 'bg-white/50'}`} />
                                            ))}
                                        </div>
                                    </>
                                )}
                                <span className={`absolute top-4 left-4 px-4 py-1 rounded-full text-sm font-semibold capitalize text-white ${villa.propertyType === 'villa' ? 'bg-purple-600' : 'bg-orange-500'}`}>
                                    {villa.propertyType}
                                </span>
                            </div>

                            <div className="p-6">
                                {loading && (
                                    <p className="text-sm text-gray-400 mb-4"><i className="fas fa-spinner fa-spin mr-2"></i>Refreshing details...</p>
                                )}
                                {error && (
                                    <p className="text-sm text-amber-600 mb-4">{error}</p>
                                )}

                                <div className="flex flex-wrap gap-4 mb-5 text-gray-600">
                                    <span><i className="fas fa-map-marker-alt text-brand-teal mr-2"></i>{villa.location}, {villa.country}</span>
                                    <span><i className="fas fa-users text-brand-teal mr-2"></i>{villa.maxGuests} Guests</span>
                                    <span><i className="fas fa-bed text-brand-teal mr-2"></i>{villa.bedrooms} Bedrooms</span>
                                    <span><i className="fas fa-bath text-brand-teal mr-2"></i>{villa.bathrooms} Bathrooms</span>
                                    {villa.rating > 0 && <span><i className="fas fa-star text-yellow-400 mr-2"></i>{villa.rating} Rating</span>}
                                </div>

                                <div className="flex items-center justify-between mb-5 pb-4 border-b">
                                    <div>
                                        <span className="text-gray-400 text-sm">Price per night</span>
                                        <div className="text-3xl font-bold bg-gradient-to-r from-brand-teal to-brand-cyan bg-clip-text text-transparent">{formatPrice(villa.pricePerNight)}</div>
                                    </div>
                                    <button
                                        type="button"
                                        onClick={() => { onClose(); onBook && onBook(villa); }}
                                        className="bg-gradient-to-r from-brand-teal to-brand-cyan text-white px-8 py-3 rounded-full font-semibold hover:shadow-lg transition transform hover:scale-105"
                                    >
                                        <i className="fas fa-calendar-check mr-2"></i>Book Now
                                    </button>
                                </div>

                                <p className="text-gray-600 mb-6 leading-relaxed">{villa.description || 'No description available.'}</p>

                                {villa.highlights?.length > 0 && (
                                    <div className="mb-5">
                                        <h4 className="text-lg font-bold text-gray-800 mb-3"><i className="fas fa-star text-brand-teal mr-2"></i>Highlights</h4>
                                        <ul className="grid grid-cols-1 md:grid-cols-2 gap-2">
                                            {villa.highlights.map((h, i) => (
                                                <li key={i} className="flex items-start gap-2 text-gray-600"><i className="fas fa-check-circle text-brand-teal mt-1 flex-shrink-0"></i>{h}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}

                                {villa.amenities?.length > 0 && (
                                    <div className="mb-5">
                                        <h4 className="text-lg font-bold text-gray-800 mb-3"><i className="fas fa-concierge-bell text-brand-teal mr-2"></i>Amenities</h4>
                                        <div className="flex flex-wrap gap-2">
                                            {villa.amenities.map((a, i) => (
                                                <span key={i} className="bg-teal-50 text-brand-teal px-3 py-1 rounded-full text-sm font-medium border border-teal-100">{a}</span>
                                            ))}
                                        </div>
                                    </div>
                                )}

                                {villa.rules?.length > 0 && (
                                    <div>
                                        <h4 className="text-lg font-bold text-gray-800 mb-3"><i className="fas fa-clipboard-list text-brand-teal mr-2"></i>House Rules</h4>
                                        <ul className="space-y-1">
                                            {villa.rules.map((r, i) => (
                                                <li key={i} className="flex items-start gap-2 text-gray-600"><i className="fas fa-circle text-brand-teal text-xs mt-2 flex-shrink-0"></i>{r}</li>
                                            ))}
                                        </ul>
                                    </div>
                                )}
                            </div>
                        </>
                    ) : (
                        <div className="text-center py-16 text-gray-500">Property not found.</div>
                    )}
                </div>
            </div>
        </div>
    );
};

export default VillaDetailsModal;
